import { useCallback, useEffect, useMemo, useState } from "react";
import { ALL_PERMISSIONS, MODULES, ROLES, def } from "@dentacore/shared-types";
import type {
  AccessRoleView,
  Effect,
  Job,
  LocationAccessView,
  ModuleKey,
  Permission,
  PersonAccessView,
} from "@dentacore/shared-types";

import { addOverride, endOverride, listRoles, personAccess, setRoles } from "../api/access";
import { updateStaff } from "../api/staff";
import { useCan } from "../auth/use-can";
import p from "../plans/procedures.module.css";
import { Modal } from "../ui/modal";
import {
  accessWith,
  choosableIn,
  impactOf,
  jobRole,
  moduleChoice,
  permissionsIn,
  planExceptions,
  viewsIn,
  withChoice,
  withPlan,
} from "./access-levels";
import type { ExceptionPlan } from "./access-levels";
import { ImpactLine, InBody, LevelList, locationShort, shortDate, usePages } from "./access-pane";
import a from "./access.module.css";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { roleLabel } from "./people-section";
import { describe, useWriter } from "./use-writer";
import { RequiredMark } from "../ui/required-mark";

/**
 * A person's access, on their page in Staff — the prototype the client
 * finalised on 2026-10-04, built.
 *
 * **Roles are given per location** (R-D6): one row per location they work at,
 * each with its own *Change* dialog, and a location where nobody gave them a
 * role shows the one for their job. **Administrator** covers every location and
 * sits above them. **Exceptions** are the person's and apply everywhere.
 * *What they can do* follows a location switch, and each part opens the reason
 * for every action in it.
 *
 * **Every change is a dialog that says what it will do before Save** — "At
 * Branch B they gain …" — and **nobody changes their own access** (RB5): on
 * your own page nothing here can be pressed, and the server refuses regardless.
 */
export function PersonAccess({
  userId,
  isSelf,
  version,
  onChanged,
  signIn,
  onAddLocation,
}: {
  userId: string;
  isSelf: boolean;
  /** Changes when the page changed something that moves their access (their locations); read again then. */
  version?: string;
  /** The person page reloads its own copy of them, which carries the Administrator flag too. */
  onChanged?: () => Promise<void>;
  /** Their Sign-in card, drawn beside Job — whether they can sign in at all sits with what they can do. */
  signIn?: React.ReactNode;
  /** Opens the Locations tab, where a location is added; absent when there is none left to add. */
  onAddLocation?: () => void;
}): React.JSX.Element {
  const can = useCan();
  const editable = can("access.assign") && !isSelf;
  const writer = useWriter();
  const { busy, toast, dismiss, fail, run } = writer;

  const [view, setView] = useState<PersonAccessView | null>(null);
  const [roles, setRoleList] = useState<AccessRoleView[]>([]);
  const [shownAt, setShownAt] = useState<string | null>(null);
  const [changing, setChanging] = useState<LocationAccessView | null>(null);
  const [adminDialog, setAdminDialog] = useState(false);
  const [adding, setAdding] = useState(false);
  const [part, setPart] = useState<string | null>(null);
  const [jobDialog, setJobDialog] = useState(false);

  const reload = useCallback(async () => {
    const [person, rows] = await Promise.all([personAccess(userId), listRoles()]);
    setView(person);
    setRoleList(rows);
    await onChanged?.();
  }, [userId, onChanged]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([personAccess(userId, controller.signal), listRoles(controller.signal)])
      .then(([person, rows]) => {
        if (controller.signal.aborted) return;
        setView(person);
        setRoleList(rows);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) fail(describe(cause, "Could not read their access."));
      });
    return () => controller.abort();
  }, [userId, fail, version]);

  const exceptions = view?.overrides ?? [];
  const ordered = [...exceptions.filter((o) => o.live), ...exceptions.filter((o) => !o.live)];
  const { shown: exShown, pager: exPager } = usePages(ordered, 3, "exceptions");

  if (view === null) {
    return (
      <>
        {/*
          Sign-in does not wait on their roles, nor go missing if those fail to
          read — and it sits **at the same place in the tree as once they have**,
          or React remounts it on arrival and a link made meanwhile is lost.
        */}
        <div className={a.personTop}>
          <section className={`${styles.card} ${styles.cardPlain}`}>
            <h3 className={styles.cardTitle}>Job</h3>
            <p className={a.hint}>Reading&hellip;</p>
          </section>
          {signIn}
        </div>
        <section className={`${styles.card} ${styles.cardPlain}`}>
          <h3 className={styles.cardTitle}>Access</h3>
          <p className={a.hint}>Reading&hellip;</p>
          {toast !== null && (
            <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />
          )}
        </section>
      </>
    );
  }

  const location = view.locations.find((l) => l.branchId === shownAt) ?? view.locations[0] ?? null;
  const heldAt = (l: LocationAccessView): Set<Permission> =>
    new Set(l.permissions.filter((x) => x.held).map((x) => x.permission));
  const held = location === null ? new Set<Permission>() : heldAt(location);
  // Roles are given per location (R-D6), and a location is added on the
  // Locations tab: said inline, so pointing there costs the page no height.
  const addLocation =
    onAddLocation === undefined ? null : (
      <button
        type="button"
        className={styles.linkish}
        onClick={onAddLocation}
        data-testid="person-access-add-location"
      >
        Add a location under Locations.
      </button>
    );
  const rolesOf = (l: LocationAccessView): AccessRoleView[] =>
    l.roles.flatMap((r) => roles.find((x) => x.id === r.id) ?? []);

  return (
    <>
      <div className={a.personTop}>
        {/*
        ── job ───────────────────────────────────────────────────────────────
        What they are clinically. It also decides their role wherever nobody
        gave them one (R-D6a), so changing it is a dialog that says what that
        does before Save (QA, 2026-10-04).
      */}
        <section className={`${styles.card} ${styles.cardPlain}`} data-testid="person-job">
          <h3 className={styles.cardTitle}>Job</h3>
          <div className={a.adminRow}>
            <span>
              <b>{roleLabel(view.job)}</b>
              {/* Half a sentence since Sign-in took the other half of the row:
                  the role it gives is said under Roles by location, below. */}
              <span className={a.muted}> · decides who can be booked as a provider</span>
            </span>
            {can("people.manage") && (
              <button
                type="button"
                className={`${p.ghost} ${a.small}`}
                disabled={busy}
                onClick={() => setJobDialog(true)}
                data-testid="person-role"
              >
                Change job
              </button>
            )}
          </div>
        </section>
        {signIn}
      </div>

      <section className={`${styles.card} ${styles.cardPlain}`} data-testid="person-access">
        <h3 className={styles.cardTitle}>Access</h3>
        {!view.isActive && (
          <p className={a.notice} data-testid="person-inactive">
            Deactivated: they hold these roles still, and can do none of it until they are
            reactivated.
          </p>
        )}
        <div className={a.accessGrid}>
          {/* ── who they are allowed to be, where ─────────────────────────── */}
          <div className={a.sub}>
            <div className={a.adminRow} data-testid="person-admin-row">
              <span>
                <b>Administrator</b> · {view.isAdministrator ? "yes, at every location" : "no"}
              </span>
              {editable && (
                <button
                  type="button"
                  className={`${p.ghost} ${a.small}`}
                  disabled={busy}
                  onClick={() => setAdminDialog(true)}
                  data-testid="person-can-administer"
                >
                  {view.isAdministrator ? "Remove" : "Make administrator"}
                </button>
              )}
            </div>

            <h4 className={a.subTitle}>Roles by location</h4>
            {view.locations.length === 0 ? (
              <p className={a.hint}>
                They do not work at any location yet.{" "}
                {addLocation ?? "Add one under Locations on this page."}
              </p>
            ) : (
              <div className={a.table} data-testid="person-access-roles">
                {view.locations.map((l) => (
                  <div key={l.branchId} className={a.locRow} data-testid="person-location-row">
                    <span className={a.ellipsis}>{l.branchName}</span>
                    <span className={l.isDefault ? `${a.ellipsis} ${a.muted}` : a.ellipsis}>
                      {l.roles.map((r) => r.name).join(", ")}
                      {l.isDefault && " — their job’s role"}
                    </span>
                    {editable ? (
                      <button
                        type="button"
                        className={`${p.ghost} ${a.small}`}
                        disabled={busy}
                        onClick={() => setChanging(l)}
                        data-testid={`person-roles-change-${l.branchName}`}
                      >
                        Change
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className={a.hint}>
              {isSelf
                ? "This is you. Nobody changes their own access — another administrator has to."
                : "Where nobody has given them a role, they get the role for their job."}
              {view.locations.length > 0 && addLocation !== null && <> {addLocation}</>}
            </p>

            <h4 className={a.subTitle}>
              Exceptions <span className={a.muted}>· every location</span>
            </h4>
            {ordered.length === 0 ? (
              <p className={a.hint}>None. They have exactly what their roles give them.</p>
            ) : (
              <>
                <div className={a.table} data-testid="person-access-overrides">
                  {exShown.map((o) => (
                    <div key={o.id} className={o.live ? a.exRow : `${a.exRow} ${a.ended}`}>
                      <span className={o.effect === "grant" ? a.grant : a.revoke}>
                        {o.effect === "grant" ? "Given" : "Taken away"}
                      </span>
                      <span className={a.exBody}>
                        <span className={a.ellipsis}>{def(o.permission).label}</span>
                        <span className={a.exMeta}>
                          &ldquo;{o.reason}&rdquo; ·{" "}
                          {o.live
                            ? o.expiresAt === null
                              ? "no end date"
                              : `until ${shortDate(o.expiresAt)}`
                            : "ended"}
                        </span>
                      </span>
                      {o.live && editable && (
                        <button
                          type="button"
                          className={p.quiet}
                          disabled={busy}
                          onClick={() =>
                            void run(() => endOverride(o.id), reload, "The exception has ended.")
                          }
                        >
                          End now
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {exPager}
              </>
            )}
            {editable && (
              <button
                type="button"
                className={p.ghost}
                style={{ width: "max-content" }}
                onClick={() => setAdding(true)}
                data-testid="person-override-open"
              >
                Add an exception
              </button>
            )}
          </div>

          {/* ── what that adds up to, at one location ─────────────────────── */}
          <div className={a.sub} data-testid="person-access-summary">
            <h4 className={a.subTitle}>
              What they can do
              {/* Buttons while they fit on a phone; a dropdown past three, which
                  a person working at many locations would otherwise push off
                  the side of the window. */}
              {view.locations.length > 3 ? (
                <select
                  className={`${p.input} ${a.locationPick}`}
                  aria-label="Location"
                  value={location?.branchId ?? ""}
                  onChange={(e) => setShownAt(e.target.value)}
                  data-testid="person-at"
                >
                  {view.locations.map((l) => (
                    <option key={l.branchId} value={l.branchId}>
                      {l.branchName}
                    </option>
                  ))}
                </select>
              ) : (
                view.locations.length > 1 && (
                  <span
                    className={a.segmented}
                    role="group"
                    aria-label="Location"
                    style={{ marginLeft: "auto" }}
                  >
                    {view.locations.map((l) => (
                      <button
                        key={l.branchId}
                        type="button"
                        className={a.segment}
                        aria-pressed={location?.branchId === l.branchId}
                        onClick={() => setShownAt(l.branchId)}
                        data-testid={`person-at-${l.branchName}`}
                      >
                        {locationShort(l.branchName)}
                      </button>
                    ))}
                  </span>
                )
              )}
            </h4>
            {location === null ? (
              <p className={a.hint}>
                {view.isAdministrator
                  ? "As an administrator they can do everything, everywhere."
                  : "Nothing yet — they work at no location."}
              </p>
            ) : (
              <>
                <p className={a.figure}>
                  {held.size}
                  <small>
                    of {ALL_PERMISSIONS.length} things at {locationShort(location.branchName)}
                  </small>
                </p>
                <LevelList held={(x) => held.has(x)} onPick={setPart} />
                <p className={a.hint}>
                  {editable && !view.isAdministrator
                    ? "Click a part to see each action and why, or to change it for them."
                    : "Click a part to see each action and why."}
                </p>
              </>
            )}
          </div>
        </div>

        {part !== null && location !== null && (
          <PartDialog
            part={part as ModuleKey}
            location={location}
            view={view}
            rolesOf={rolesOf}
            editable={editable}
            busy={busy}
            onClose={() => setPart(null)}
            onSave={(plan, reason, expiresAt) => {
              void run(
                async () => {
                  // Ending first frees the one-live-per-permission index for
                  // an exception that replaces it (PA-D2).
                  for (const o of plan.end) await endOverride(o.id);
                  for (const x of plan.add) await addOverride(userId, { ...x, reason, expiresAt });
                },
                reload,
                "Saved, at every location. It applies from their next click.",
              ).then((ok) => {
                if (ok) setPart(null);
                // A change that failed half way is still shown as it now stands.
                else void reload();
              });
            }}
          />
        )}

        {changing !== null && (
          <RolesDialog
            location={changing}
            roles={roles}
            job={view.job}
            isAdministrator={view.isAdministrator}
            before={heldAt(changing)}
            overrides={view.overrides}
            busy={busy}
            onClose={() => setChanging(null)}
            onSave={(ids) => {
              void run(
                () => setRoles(userId, changing.branchId, ids),
                reload,
                "Saved. It applies from their next click.",
              ).then((ok) => {
                if (ok) setChanging(null);
              });
            }}
          />
        )}

        {adminDialog && (
          <AdminDialog
            giving={!view.isAdministrator}
            before={new Set(view.locations.flatMap((l) => [...heldAt(l)]))}
            after={
              view.isAdministrator
                ? new Set(
                    view.locations.flatMap((l) => [
                      ...accessWith(rolesOf(l), false, view.overrides),
                    ]),
                  )
                : new Set(ALL_PERMISSIONS)
            }
            busy={busy}
            onClose={() => setAdminDialog(false)}
            onConfirm={() => {
              void run(
                () => updateStaff(userId, { canAdminister: !view.isAdministrator }),
                reload,
                view.isAdministrator
                  ? "They are no longer an administrator."
                  : "They are now an administrator.",
              ).then((ok) => {
                if (ok) setAdminDialog(false);
              });
            }}
          />
        )}

        {adding && (
          <ExceptionDialog
            held={held}
            where={location === null ? "" : locationShort(location.branchName)}
            busy={busy}
            onClose={() => setAdding(false)}
            onSave={(body) => {
              void run(
                () => addOverride(userId, body),
                reload,
                body.effect === "grant"
                  ? "Given, at every location. It applies from their next click."
                  : "Taken away, at every location. It applies from their next click.",
              ).then((ok) => {
                if (ok) setAdding(false);
              });
            }}
          />
        )}

        {jobDialog && (
          <JobDialog
            view={view}
            roles={roles}
            isSelf={isSelf}
            mayChangeAccess={can("access.assign")}
            busy={busy}
            onClose={() => setJobDialog(false)}
            onSave={(job) => {
              void run(() => updateStaff(userId, { role: job }), reload, "Job changed.").then(
                (ok) => {
                  if (ok) setJobDialog(false);
                },
              );
            }}
          />
        )}

        {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
      </section>
    </>
  );
}

/**
 * A new job, and what it does: wherever nobody gave them a role they hold
 * their job's role (R-D6a), so the dialog lists each such location with the
 * role it moves from and to and what they gain or lose there.
 */
function JobDialog({
  view,
  roles,
  isSelf,
  mayChangeAccess,
  busy,
  onClose,
  onSave,
}: {
  view: PersonAccessView;
  roles: readonly AccessRoleView[];
  isSelf: boolean;
  mayChangeAccess: boolean;
  busy: boolean;
  onClose: () => void;
  onSave: (job: Job) => void;
}): React.JSX.Element {
  const [job, setJob] = useState<Job>(view.job);
  const from = jobRole(roles, view.job);
  const to = jobRole(roles, job);
  const moves =
    job === view.job || view.isAdministrator || from?.id === to?.id
      ? []
      : view.locations.filter((l) => l.isDefault);
  const blocked =
    moves.length > 0
      ? isSelf
        ? "This would change your own access — another administrator has to."
        : !mayChangeAccess
          ? "This changes their access, so an administrator has to make it."
          : null
      : null;
  return (
    <InBody>
      <Modal label="Change job" onClose={onClose} testId="person-job-dialog">
        <h2 className={p.dialogTitle}>Change job</h2>
        <label className={p.field}>
          <span className={p.fieldLabel}>
            Job
            <RequiredMark />
          </span>
          <select
            className={p.input}
            value={job}
            onChange={(event) => setJob(event.target.value as Job)}
            data-testid="person-job-select"
          >
            {ROLES.map((name) => (
              <option key={name} value={name}>
                {roleLabel(name)}
              </option>
            ))}
          </select>
        </label>
        {job !== view.job && moves.length === 0 && (
          <p className={a.impact}>
            Their access does not change: every location they work at has a role given to them
            {view.isAdministrator ? ", and they are an administrator" : ""}.
          </p>
        )}
        {moves.map((l) => (
          <ImpactLine
            key={l.branchId}
            impact={impactOf(
              new Set(l.permissions.filter((x) => x.held).map((x) => x.permission)),
              accessWith(to === undefined ? [] : [to], view.isAdministrator, view.overrides),
            )}
            lead={
              <>
                At {locationShort(l.branchName)} they move from <b>{from?.name}</b> to{" "}
                <b>{to?.name}</b>, so they
              </>
            }
          />
        ))}
        {blocked !== null && <p className={a.notice}>{blocked}</p>}
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={busy || job === view.job || blocked !== null}
            onClick={() => onSave(job)}
            data-testid="person-job-save"
          >
            Save
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

/** Their roles at one location, ticked, with what changes said before Save. */
function RolesDialog({
  location,
  roles,
  job,
  isAdministrator,
  before,
  overrides,
  busy,
  onClose,
  onSave,
}: {
  location: LocationAccessView;
  roles: readonly AccessRoleView[];
  job: string;
  isAdministrator: boolean;
  before: ReadonlySet<Permission>;
  overrides: PersonAccessView["overrides"];
  busy: boolean;
  onClose: () => void;
  onSave: (roleIds: string[]) => void;
}): React.JSX.Element {
  const [ids, setIds] = useState<ReadonlySet<string>>(
    new Set(location.isDefault ? [] : location.roles.map((r) => r.id)),
  );
  const forJob = jobRole(roles, job);
  const after = useMemo(() => {
    const chosen =
      ids.size > 0 ? roles.filter((r) => ids.has(r.id)) : forJob === undefined ? [] : [forJob];
    return accessWith(chosen, isAdministrator, overrides);
  }, [ids, roles, forJob, isAdministrator, overrides]);
  const where = locationShort(location.branchName);

  return (
    <InBody>
      <Modal
        label={`Roles at ${location.branchName}`}
        onClose={onClose}
        testId="person-roles-dialog"
      >
        <h2 className={p.dialogTitle}>Roles at {location.branchName}</h2>
        <div className={a.checkList}>
          {roles.map((r) => (
            <label key={r.id} className={a.check}>
              <input
                type="checkbox"
                checked={ids.has(r.id)}
                onChange={() => {
                  const next = new Set(ids);
                  if (next.has(r.id)) next.delete(r.id);
                  else next.add(r.id);
                  setIds(next);
                }}
                data-testid={`person-role-${r.name}`}
              />
              <span>
                {r.name}
                {r.description !== null && <span className={a.checkNote}>{r.description}</span>}
              </span>
            </label>
          ))}
        </div>
        {ids.size === 0 && (
          <p className={a.notice}>
            No role ticked: here they get the role for their job
            {forJob === undefined ? "" : `, “${forJob.name}”`}.
          </p>
        )}
        {isAdministrator && (
          <p className={a.hint}>
            As an administrator they can do everything whatever their roles say.
          </p>
        )}
        <ImpactLine impact={impactOf(before, after)} lead={<>At {where} they</>} />
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={busy}
            onClick={() => onSave([...ids])}
            data-testid="person-roles-save"
          >
            Save
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

function AdminDialog({
  giving,
  before,
  after,
  busy,
  onClose,
  onConfirm,
}: {
  giving: boolean;
  before: ReadonlySet<Permission>;
  after: ReadonlySet<Permission>;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  return (
    <InBody>
      <Modal label="Administrator" onClose={onClose} testId="person-admin-dialog">
        <h2 className={p.dialogTitle}>
          {giving ? "Make them an administrator?" : "Remove them as administrator?"}
        </h2>
        <p className={a.hint}>
          {giving
            ? "They can then do everything at every location, including giving people access."
            : "They keep only what their roles give them at each location. The clinic always keeps at least one administrator."}
        </p>
        <ImpactLine impact={impactOf(before, after)} lead={<>Across their locations they</>} />
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={busy}
            onClick={onConfirm}
            data-testid="person-admin-confirm"
          >
            {giving ? "Make administrator" : "Remove"}
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

/** One thing given or taken away, picked from a searchable, paged list. */
function ExceptionDialog({
  held,
  where,
  busy,
  onClose,
  onSave,
}: {
  held: ReadonlySet<Permission>;
  where: string;
  busy: boolean;
  onClose: () => void;
  onSave: (body: {
    permission: Permission;
    effect: Effect;
    reason: string;
    expiresAt: string | null;
  }) => void;
}): React.JSX.Element {
  const [effect, setEffect] = useState<Effect>("grant");
  const [query, setQuery] = useState("");
  const [permission, setPermission] = useState<Permission | null>(null);
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const needle = query.trim().toLocaleLowerCase();
  const options = MODULES.flatMap((m) =>
    permissionsIn(m.key)
      .filter((x) => def(x).risk !== "critical")
      .filter(
        (x) =>
          needle === "" ||
          def(x).label.toLocaleLowerCase().includes(needle) ||
          m.label.toLocaleLowerCase().includes(needle),
      )
      .map((x) => ({ permission: x, part: m.label })),
  );
  const { shown, pager } = usePages(options, 6, "actions", needle);
  const hint =
    permission === null
      ? "Choose what to give or take away."
      : held.has(permission) && effect === "grant"
        ? "They already have this here, so giving it may change nothing."
        : effect === "revoke"
          ? "Taking something away also takes away what depends on it."
          : "Anything it depends on comes with it.";

  return (
    <InBody>
      <Modal label="Add an exception" onClose={onClose} testId="person-override-form">
        <h2 className={p.dialogTitle}>Add an exception</h2>
        <div className={a.segmented} role="group" aria-label="Give or take away">
          <button
            type="button"
            className={a.segment}
            aria-pressed={effect === "grant"}
            onClick={() => setEffect("grant")}
          >
            Give
          </button>
          <button
            type="button"
            className={a.segment}
            aria-pressed={effect === "revoke"}
            onClick={() => setEffect("revoke")}
          >
            Take away
          </button>
        </div>
        <label className={p.field}>
          <span className={p.fieldLabel}>
            What
            <RequiredMark />
          </span>
          <input
            type="search"
            className={p.input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search — invoice, notes, rota…"
            data-testid="person-override-search"
          />
        </label>
        <div className={a.pick} role="listbox" aria-label="What to give or take away">
          {shown.map((o) => (
            <button
              key={o.permission}
              type="button"
              role="option"
              aria-selected={permission === o.permission}
              className={a.pickOption}
              onClick={() => setPermission(o.permission)}
              data-testid={`person-override-pick-${o.permission}`}
            >
              <span>
                {def(o.permission).label}
                <span className={a.pickPart}>{o.part}</span>
              </span>
              <span className={held.has(o.permission) ? a.has : a.hasNot}>
                {held.has(o.permission)
                  ? `Has it${where === "" ? "" : ` at ${where}`}`
                  : "Doesn’t have it"}
              </span>
            </button>
          ))}
          {options.length === 0 && <p className={p.blank}>Nothing matches.</p>}
        </div>
        {pager}
        <div className={p.pair}>
          <label className={p.field}>
            <span className={p.fieldLabel}>
              Why
              <RequiredMark />
            </span>
            <input
              className={p.input}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Covering the front desk while they are on leave"
              data-testid="person-override-reason"
            />
          </label>
          <label className={p.field}>
            <span className={p.fieldLabel}>Until</span>
            <input
              type="date"
              className={p.input}
              value={until}
              onChange={(event) => setUntil(event.target.value)}
              data-testid="person-override-until"
            />
          </label>
        </div>
        <p className={a.hint}>
          {hint} It applies at every location they work. Giving people access itself is for roles
          only.
        </p>
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={permission === null || reason.trim() === "" || busy}
            onClick={() => {
              if (permission === null) return;
              onSave({
                permission,
                effect,
                reason: reason.trim(),
                expiresAt: until === "" ? null : new Date(`${until}T23:59:59`).toISOString(),
              });
            }}
            data-testid="person-override-save"
          >
            {effect === "grant" ? "Give" : "Take away"}
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

const partLabel = (part: ModuleKey): string => MODULES.find((m) => m.key === part)?.label ?? part;

/**
 * One part of DentaCore for one person (PA-D1–PA-D3, STORY-193) — what the
 * *What they can do* grid opens. Read-only it is the old "Why" list; for
 * somebody with `access.assign` each action is a switch and the part has
 * None / View / Full, **written as that person's exceptions** (roles stay the
 * base, R-D6).
 *
 * **Undo before adding** (PA-D2): turning an action back to what their roles
 * give ends the exception that changed it rather than stacking an opposite
 * one. **It says what it does before Save** (PA-D3), at every location they
 * work, because an exception follows them everywhere.
 */
function PartDialog({
  part,
  location,
  view,
  rolesOf,
  editable,
  busy,
  onClose,
  onSave,
}: {
  part: ModuleKey;
  location: LocationAccessView;
  view: PersonAccessView;
  rolesOf: (l: LocationAccessView) => AccessRoleView[];
  editable: boolean;
  busy: boolean;
  onClose: () => void;
  onSave: (plan: ExceptionPlan, reason: string, expiresAt: string | null) => void;
}): React.JSX.Element {
  const [want, setWant] = useState<ReadonlyMap<Permission, boolean>>(new Map());
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");

  const live = view.overrides.filter((o) => o.live);
  const fromRoles = accessWith(rolesOf(location), false, []);
  // Both sides worked out here, never the server's `held`: a deactivated
  // person's page holds only the floor, which would make every switch a change.
  const heldNow = accessWith(rolesOf(location), false, live);
  const plan = planExceptions(want, fromRoles, heldNow, live);
  const simulated = withPlan(live, plan);
  const heldAfter = accessWith(rolesOf(location), false, simulated);
  const whyOf = new Map(location.permissions.map((x) => [x.permission, x.why]));
  // Administrators hold everything; access itself is for roles only (PA-D3).
  const canChange = editable && !view.isAdministrator;
  const switchable = (p: Permission): boolean =>
    canChange && def(p).floor !== true && def(p).risk !== "critical";
  const changes = plan.end.length + plan.add.length;

  const impacts = view.locations
    .map((l) => ({
      name: locationShort(l.branchName),
      ...impactOf(accessWith(rolesOf(l), false, live), accessWith(rolesOf(l), false, simulated)),
    }))
    .filter((row) => row.gain.length + row.lose.length > 0);

  const setLevel = (level: "none" | "view" | "full"): void => {
    const target = withChoice(new Set<Permission>(), part, level);
    setWant((prev) => {
      const next = new Map(prev);
      for (const p of choosableIn(part).filter(switchable)) next.set(p, target.has(p));
      return next;
    });
  };
  const flip = (p: Permission): void => {
    // From the previous state, so two quick clicks never drop the first.
    setWant((prev) => new Map(prev).set(p, !heldAfter.has(p)));
  };
  const level = moduleChoice((p) => heldAfter.has(p), part);

  return (
    <InBody>
      <Modal
        label={`${partLabel(part)} for this person`}
        onClose={onClose}
        panelClassName={a.dialogWide}
        testId="person-part"
      >
        <h2 className={p.dialogTitle}>
          {partLabel(part)} — at {locationShort(location.branchName)}
        </h2>
        {canChange && (
          <div className={a.segmented} role="group" aria-label="How much of it">
            {(["none", ...(viewsIn(part).length > 0 ? ["view"] : []), "full"] as const).map(
              (choice) => (
                <button
                  key={choice}
                  type="button"
                  className={a.segment}
                  aria-pressed={level === choice}
                  onClick={() => setLevel(choice as "none" | "view" | "full")}
                  data-testid={`person-part-${choice}`}
                >
                  {choice === "none" ? "None" : choice === "view" ? "View" : "Full"}
                </button>
              ),
            )}
          </div>
        )}
        {/* Two columns past six actions, so Staff's twelve fit a 768 window with no scrolling (PF4). */}
        <ul
          className={permissionsIn(part).length > 6 ? `${a.why} ${a.whyTwo}` : a.why}
          data-testid="person-why"
        >
          {permissionsIn(part).map((x) => {
            const on = heldAfter.has(x);
            const moved = on !== heldNow.has(x);
            const mark = (
              <span className={on ? `${a.mark} ${a.markYes}` : `${a.mark} ${a.markNo}`}>
                {on ? "✓" : "–"}
              </span>
            );
            const words = (
              <span>
                {def(x).label}
                <span className={a.checkNote}>
                  {moved ? (on ? "Given to them here" : "Taken away from them here") : whyOf.get(x)}
                </span>
              </span>
            );
            return (
              <li key={x}>
                {switchable(x) ? (
                  <button
                    type="button"
                    className={a.whySwitch}
                    aria-pressed={on}
                    onClick={() => flip(x)}
                    data-testid={`person-part-${x}`}
                  >
                    {mark}
                    {words}
                  </button>
                ) : (
                  <>
                    {mark}
                    {words}
                  </>
                )}
              </li>
            );
          })}
        </ul>
        {canChange && changes > 0 && (
          <>
            {impacts.length === 0 ? (
              <ImpactLine impact={{ gain: [], lose: [] }} lead="" />
            ) : (
              impacts.map((row) => (
                <ImpactLine
                  key={row.name}
                  impact={row}
                  lead={
                    <>
                      At <b>{row.name}</b> they
                    </>
                  }
                />
              ))
            )}
            <p className={a.hint}>
              Saved as{" "}
              {changes === 1 ? "one exception change" : `${String(changes)} exception changes`}.
              Exceptions follow them to every location they work.
            </p>
            {plan.add.length > 0 && (
              <div className={p.pair}>
                <label className={p.field}>
                  <span className={p.fieldLabel}>
                    Why
                    <RequiredMark />
                  </span>
                  <input
                    className={p.input}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Covering the front desk while they are on leave"
                    data-testid="person-part-reason"
                  />
                </label>
                <label className={p.field}>
                  <span className={p.fieldLabel}>Until</span>
                  <input
                    type="date"
                    className={p.input}
                    value={until}
                    onChange={(event) => setUntil(event.target.value)}
                    data-testid="person-part-until"
                  />
                </label>
              </div>
            )}
          </>
        )}
        {canChange && !view.isActive && (
          <p className={a.notice}>Deactivated: this is what they will have once reactivated.</p>
        )}
        {view.isAdministrator && editable && (
          <p className={a.hint}>An administrator can do everything. Remove administrator first.</p>
        )}
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            {canChange && changes > 0 ? "Cancel" : "Close"}
          </button>
          {canChange && (
            <button
              type="button"
              className={p.primary}
              // A reason is asked only of what is added; ending an exception needs none.
              disabled={busy || changes === 0 || (plan.add.length > 0 && reason.trim() === "")}
              onClick={() =>
                onSave(
                  plan,
                  reason.trim(),
                  until === "" ? null : new Date(`${until}T23:59:59`).toISOString(),
                )
              }
              data-testid="person-part-save"
            >
              Save
            </button>
          )}
        </div>
      </Modal>
    </InBody>
  );
}
